import { useState, useEffect, useCallback } from 'react';
import { WorkPillExecution } from '../../../types/navigationTypes';
import { ExecutionService } from '../services/executionService';
import { configService } from '../../../shared/services/configService';

export interface UseUserExecutionsReturn {
  executions: WorkPillExecution[];
  loading: boolean;
  error: string | null;
  selectedExecutionId: string | null;
  selectExecution: (executionId: string) => void;
  refreshExecutions: () => Promise<void>;
  updateExecutionName: (executionId: string, newName: string) => Promise<boolean>;
}

/**
 * Hook para manejar las ejecuciones asignadas a un usuario
 */
export function useUserExecutions(): UseUserExecutionsReturn {
  const [executions, setExecutions] = useState<WorkPillExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedExecutionId, setSelectedExecutionId] = useState<string | null>(null);

  const loadExecutions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const userId = configService.getUserId();
      
      // Si no hay usuario autenticado, no cargar ejecuciones
      if (!userId) {
        console.warn('⚠️ No hay usuario autenticado, no se pueden cargar ejecuciones');
        setExecutions([]);
        setLoading(false);
        return;
      }
      
      const userExecutions = await ExecutionService.getUserExecutions(userId);
      
      setExecutions(userExecutions);

      // Si hay ejecuciones y hay una execution_id en config, seleccionarla automáticamente
      if (userExecutions.length > 0) {
        const configExecutionId = configService.getExecutionId();
        
        // Si hay un executionId configurado y existe en la lista, seleccionarlo
        if (configExecutionId && configExecutionId.length > 0) {
          const executionExists = userExecutions.some(ex => ex.id === configExecutionId);
          
          if (executionExists) {
            setSelectedExecutionId(configExecutionId);
          } else {
            // Si no existe la ejecución del config, no seleccionar ninguna (usuario debe elegir)
            setSelectedExecutionId(null);
          }
        } else {
          // No hay ejecución configurada, no seleccionar ninguna (usuario debe elegir)
          setSelectedExecutionId(null);
        }
      }
    } catch (err) {
      console.error('Error loading user executions:', err);
      setError('Error al cargar las ejecuciones del usuario');
    } finally {
      setLoading(false);
    }
  }, []); // Sin dependencias porque configService es estable

  useEffect(() => {
    loadExecutions();
  }, [loadExecutions]);

  const selectExecution = (executionId: string) => {
    setSelectedExecutionId(executionId);
  };

  const refreshExecutions = useCallback(async () => {
    await loadExecutions();
  }, [loadExecutions]);

  const updateExecutionName = async (executionId: string, newName: string) => {
    const success = await ExecutionService.updateExecutionName(executionId, newName);
    if (success) {
      await refreshExecutions();
    }
    return success;
  };

  return {
    executions,
    loading,
    error,
    selectedExecutionId,
    selectExecution,
    refreshExecutions,
    updateExecutionName
  };
}



